import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";
import React, { useEffect, useState } from "react";
import { Box, Chip, ChipProps, GlobalStyles, useTheme, useMediaQuery } from "@mui/material";
import { getStatusKeyChipStyle } from "../../utils/status-chip-colors";
import { Link as MuiLink, Button, Table, TableBody, TableCell, TableRow } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { setNotificationAsReadOrReceived } from "../../api/notification";
import { formatDate, showNotification } from "../../utils/helper";
import { tokens } from "../layout/theme";
import { translations } from "../../data/mockData";
import { useTranslation } from "react-i18next";
import "../../i18n";
import {
  DataGrid,
  GridColDef,
  GridEventListener,
  GridFilterItem,
  GridFilterModel,
  GridOverlay,
  GridPaginationModel,
  GridSortItem,
  GridSortModel,
  GridToolbarFilterButton,
} from "@mui/x-data-grid";
import { useGridApiRef } from "@mui/x-data-grid";
import { TaskType } from "../../models/tasks/task-status.enum";
import useFiltersStore from "../../stores/store-filters";

/**
 * Extended GridColDef interface that adds a hide property for column visibility control.
 * @extends {GridColDef}
 * @property {boolean} [hide] - Optional flag to hide the column
 */
type ExtendedGridColDef = GridColDef & { hide?: boolean };

/**
 * Interface defining the props for the DataTable component.
 * @property {any[]} data - The data to display in the table
 * @property {ExtendedGridColDef[]} fieldsNames - Column definitions for the table
 * @property {string} [parentPath] - Base path for row links
 * @property {number} [boxHeight] - Height of the table container in pixels
 * @property {string} [pxHeight] - Height of the table in pixels
 * @property {boolean} [audits] - Whether the table displays audit data
 * @property {boolean} [notifications] - Whether the table displays notifications
 * @property {number} [currentPage] - Current page number for pagination
 * @property {number} [totalItems] - Total number of items for pagination
 * @property {number} [itemsPerPage] - Number of items per page
 * @property {string} [noDataMessage] - Message to display when no data is available
 * @property {boolean} [disableColumnFilter] - Whether to disable column filtering
 * @property {boolean} [ssrFilter] - Whether to use server-side filtering
 * @property {boolean} [ssrSort] - Whether to use server-side sorting
 * @property {boolean} [isLoading] - Whether the table is in a loading state
 * @property {boolean} [showFilterPanel] - Whether to show the filter panel
 * @property {boolean} [notes] - Whether the table displays notes
 * @property {(newPage: any) => void} [onPageChange] - Callback for page changes
 * @property {(filterItems: GridFilterItem | any) => void} [onFilterChange] - Callback for filter changes
 * @property {(sortModel: GridSortItem | any) => void} [onSortChange] - Callback for sort changes
 * @property {() => void} [refetch] - Callback to refetch data
 * @property {string} [customLink] - Custom link format for rows
 * @property {string} [filterKey] - Key for storing filters in the store
 * @property {React.ReactNode} [customToolbarContent] - Custom content to display in the toolbar
 */
type Props = {
  data: any;
  fieldsNames: ExtendedGridColDef[];
  parentPath?: string | ((row: any) => string);
  boxHeight?: number;
  minHeight?: number;
  pxHeight?: string;
  audits?: boolean;
  notifications?: boolean;
  currentPage?: number;
  totalItems?: number;
  itemsPerPage?: number;
  noDataMessage?: string;
  disableColumnFilter?: boolean;
  ssrFilter?: boolean;
  ssrSort?: boolean;
  isLoading?: boolean;
  showFilterPanel?: boolean;
  notes?: boolean;
  defaultSortField?: string;
  defaultSortDirection?: "asc" | "desc";
  onPageChange?: (newPage: any) => void;
  onFilterChange?: (filterItems: GridFilterItem | any) => void;
  onSortChange?: (sortModel: GridSortItem | any) => void;
  refetch?: () => void;
  customLink?: string;
  filterKey?: string;
  customToolbarContent?: React.ReactNode;
  getCellClassName?: (params: any) => string;
  getRowHeight?: () => number | "auto";
};

/**
 * A reusable data table component built on Material-UI's DataGrid.
 * 
 * This component provides a feature-rich table interface with support for:
 * - Pagination
 * - Sorting
 * - Filtering
 * - Custom row actions
 * - Server-side operations
 * - Internationalization
 * - Custom styling
 * 
 * @component
 * @param {Props} props - The props passed to the component
 * 
 * @example
 * ```tsx
 * <DataTable
 *   data={tableData}
 *   fieldsNames={columns}
 *   parentPath="/clients"
 *   currentPage={1}
 *   totalItems={100}
 *   itemsPerPage={10}
 *   onPageChange={handlePageChange}
 *   onFilterChange={handleFilterChange}
 *   onSortChange={handleSortChange}
 * />
 * ```
 * 
 * @remarks
 * - Uses Material-UI DataGrid component
 * - Supports server-side and client-side operations
 * - Implements custom filtering and sorting
 * - Provides notification handling
 * - Supports audit trail display
 * - Handles internationalization
 * - Maintains consistent styling with the application theme
 * 
 * @returns {JSX.Element} A React component that renders a data table
 */
const DataTable = ({
  parentPath,
  data,
  fieldsNames,
  boxHeight,
  minHeight,
  pxHeight,
  audits,
  notifications,
  notes,
  currentPage,
  totalItems,
  itemsPerPage,
  disableColumnFilter,
  ssrFilter = false,
  ssrSort = false,
  isLoading = false,
  showFilterPanel = false,
  onPageChange,
  onFilterChange,
  onSortChange,
  refetch,
  customLink,
  filterKey,
  customToolbarContent,
  noDataMessage,
  getCellClassName,
  getRowHeight,
  defaultSortField, //set column to default sort by
  defaultSortDirection, // asc or desc
  
}: Props) => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const visibleColumns = fieldsNames.filter((column) => !column.hide);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: currentPage ?? 0,
    pageSize: itemsPerPage ?? 0,
    
  });
  // eslint-disable-next-line
  const [currentItemPerPage, setCurrentItemPerPage] = useState(itemsPerPage);
  const pageSizeOptions = Array.from(
    new Set([currentItemPerPage ? currentItemPerPage : 10, 15, 25, 50])
  );
  const [rowCountState, setRowCountState] = useState(totalItems);

  //define default sort to be asc for the first column, if that doesnt work use second column
  //also sets other default behavior for sorting

const dueDateColumn = visibleColumns.find(col => col.field === "due_date" && col.sortable !== false);
const firstSortableColumn = defaultSortField
  ? { field: defaultSortField }
  : dueDateColumn ?? visibleColumns.find(col => col.sortable !== false);

const defaultDirection = defaultSortDirection ?? (dueDateColumn && !defaultSortField ? "desc" : "asc");

const [sortModel, setSortModel] = useState<GridSortModel>(
  firstSortableColumn ? [{ field: firstSortableColumn.field, sort: defaultDirection }] : []
);

  // Filters state
  const { filters, setFilters, clearFilters } = useFiltersStore((state) => state);
  const filterModal = filters[filterKey ?? ''];
  const [ currentFilterModal, setCurrentFilterModal ] = useState(filterModal);

  const apiRef = useGridApiRef();

  const defaultMinHeight = 0;
  useEffect(() => {
    setCurrentFilterModal(filterModal);
  }, [filterModal]);

  useEffect(() => {
    if (showFilterPanel) {
      apiRef.current.showFilterPanel();
    }
  }, [apiRef, showFilterPanel]);

  useEffect(() => {
    setRowCountState((prevRowCountState: any) =>
      totalItems !== undefined ? totalItems : prevRowCountState
    );
  }, [totalItems, setRowCountState]);

useEffect(() => {
  if (onSortChange && firstSortableColumn) {
    onSortChange({ field: firstSortableColumn.field, sort: defaultDirection });
  }
}, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRowClick: GridEventListener<"rowClick"> = async (params) => {
    if (
      (!params.row?.parent_task_id &&
        params.row?.task_type === TaskType.wiederkehrend)
      || params.row.repeated_type === "recurring_task"
    )
      // if the row presents a task of type recurring, it should not be clickable
      return;
    if (notifications) {
      const rowData = params.row; // Access the row data directly
      await showNotification(rowData.type, rowData.body, rowData.title, false);
      setNotificationAsReadOrReceived(rowData.id, true);
      if (refetch) refetch();
    } else if (notes) {
      const rowData = params.row; // Access the row data directly
      await showNotification("normal", rowData.text, rowData.name, false);
    } else if (audits) {
      const rowData = params.row;
      let html = "";
      const changes = [] as any;
      for (const [key1] of Object.entries(rowData.old_values_all)) {
        for (const [key2, value2] of Object.entries(
          rowData.old_values_all[key1]
        )) {
          changes[key2] = {
            from: value2,
            to: null,
          };
        }
      }
      for (const [key1] of Object.entries(rowData.new_values_all)) {
        for (const [key2, value2] of Object.entries(
          rowData.new_values_all[key1]
        )) {
          if (changes[key2]) {
            changes[key2].to = value2;
          } else {
            changes[key2] = {
              from: null,
              to: value2,
            };
          }
        }
      }

      delete changes['id'];
      delete changes['plant_id'];
      delete changes['created_by'];
      delete changes['component_type'];

      for (const key in changes) {
        const keyValue = t(`components:fields.${key}` as any);
        const fromValue = i18n.exists(
          `components:fields.${key}.values.${changes[key].from}`
        )
          ? t(`components:fields.${key}.values.${changes[key].from}`)
          : changes[key].from;
        const toValue = i18n.exists(
          `components:fields.${key}.values.${changes[key].to}`
        )
          ? t(`components:fields.${key}.values.${changes[key].to}`)
          : changes[key].to;
        if (key !== "image") {
          html += `<b>${keyValue}:</b> (${
            fromValue ?? "-"
            }) <b>TO</b> (${toValue})</br></br>`;
        } else {
          html += `<b>${keyValue}.</b> </br></br>`;
        }
      }
      html = `<div style='text-align:left'>${html}</div>`;
      await showNotification("normal", html, rowData.user_name, false, true);
    }
  };

  const getLinkWithIds = (link: string, data: any) => {
    if (link.includes("{client_id}")) {
      link = link.replace(
        "{client_id}",
        data?.client_id || data?.building?.client?.id
      );
    }
    if (link.includes("{building_id}")) {
      link = link.replace(
        "{building_id}",
        data?.building_id ||
        data?.plant?.building_id ||
        data?.building?.id
      );
    }
    if (link.includes("{plant_id}")) {
      link = link.replace(
        "{plant_id}",
        data?.plant_id || data?.plant?.id
      );
    }
    if (link.includes("{disturbances_id}")) {
      link = link.replace("{disturbances_id}", data?.id);
    }
    if (link.includes("{task_id}")) {
      link = link.replace("{task_id}", data?.id);
    }

    return link;
  }

  const handleSortModelChange = (sortModel: GridSortModel) => {
    setSortModel(sortModel); // add this line
    if (onSortChange) {
      onSortChange(sortModel[0]);
    }
  };

  const CustomNoRowsOverlay = () => {
    return (
      <GridOverlay>
        <div style={{ textAlign: "center" }}>{noDataMessage || "Keine Datensätze"}</div>
      </GridOverlay>
    );
  };

  const handleFilterModelChange = (filterModel: GridFilterModel) => {
    if (filterKey) {
      setFilters(filterKey, filterModel);
    } else if(onFilterChange) {
      onFilterChange(filterModel?.items?.[0]);
      setCurrentFilterModal(filterModel);
    }
  };

  const CustomToolbar = () => {
    return (
      <Box
        display="flex"
        flexDirection={{ xs: "column", sm: "row" }}
        justifyContent={{ xs: "flex-start", sm: "flex-end" }}
        alignItems={{ xs: "stretch", sm: "center" }}
        gap={2}
        padding="8px 30px"
        width="100%"
        flexWrap="wrap"
      >
        {/* Custom content in the center */}

        {customToolbarContent}

        {/* Default filter controls positioned absolutely on the right */}

        {currentFilterModal && (
          <Button
            variant="text"
            startIcon={<FilterAltOffIcon />}
            sx={{ color: colors.blueAccent[200] }}
            onClick={() => {
              if (filterKey) {
                clearFilters(filterKey);
                } else if(onFilterChange) {
                onFilterChange({});
                setCurrentFilterModal(undefined);
              }
            }}
          >
            Filter löschen
          </Button>
        )}

        {showFilterPanel && ( //disable old filter
  <GridToolbarFilterButton sx={{ color: colors.blueAccent[200] }} />
  )}
      </Box>
    );
  };

  return (
    <>
      <GlobalStyles
        styles={{
          ".MuiDataGrid-menu": { zIndex: "10001 !important" },
          ".MuiDataGridMenu-root": { zIndex: "10001 !important" },
          ".MuiDataGridPanel-root": { zIndex: "10001 !important" },
        }}
      />
    <Box
      sx={{
        // height: "100%",
        minHeight: minHeight ?? defaultMinHeight,

        width: "100%",
        "& .MuiDataGrid-root": {
          border: "none",
        },
        "& .MuiDataGrid-main": {
          background: colors.primary[400],
        },
        "& .super-app-theme--header": {
          backgroundColor: colors.blueAccent[700],
          fontSize: "17px",
        },
        "& .super-app-theme--cell": {
          fontSize: "16px",
          "&.urgent-cell": {
            backgroundColor: "red",
          },
          "&.normal-cell": {
            backgroundColor: "green",
          },
          "&.alert-cell": {
            backgroundColor: "yellow",
          },
          whiteSpace: "nowrap !important",
          overflow: "hidden !important",
          textOverflow: "ellipsis !important",
        },
        "& .MuiDataGrid-footerContainer": {
          border: "none",
          borderBottomLeftRadius: "4px",
          borderBottomRightRadius: "4px",
          backgroundColor: colors.blueAccent[700],
        },
        overflowY: "hidden", // Hide vertical scroll
      }}
    >
      {/* {data?.length ? ( */}
      <DataGrid
        apiRef={apiRef}
        rows={data}
        autoHeight={false}
        // getRowHeight={() => "auto"}
        columns={[
          ...visibleColumns.map((column) => {
            if (
              column.field === "type" ||
              column.field === "taskStatus" ||
              column.field === "subTaskStatus" ||
              column.field === "status" ||
              column.field === "actionStatus" ||
              column.field === "disturbanceStatus" ||
              column.field === "userStatus"
            ) {
              return {
                ...column,
                renderCell: (params: any) => {
                  let chipColor: ChipProps["color"] = "success"; // Default color
                  let chipStyle: React.CSSProperties = {}; // Custom style for dark/light mode
                  if (column.field === "taskStatus" || column.field === "subTaskStatus" || column.field === 'status') {
                    // Workflows whose phase names collide with another workflow's pick
                    // their colour by status key instead; everything else falls through
                    // to the name-based rules below.
                    const keyedStyle = getStatusKeyChipStyle(
                      params.row?.status_key,
                      colors,
                      theme.palette.mode
                    );
                    if (keyedStyle) {
                      chipStyle = keyedStyle;
                    } else
                    switch (params.value) {
                      case "Neu":
                      case "Aktiv":
                        chipStyle = {
                          backgroundColor: theme.palette.mode === "dark" ? "lightblue" : "white",
                          color: "black",
                        };
                        break;
                      case "Verzögert":
                        chipColor = "warning";
                        break;
                      case "abgeschlossen":
                      case "Bereit zur Überprüfung":
                      case "Fertig":
                        chipColor = "success";
                        break;
                      case "weiter bearbeiten":
                        chipColor = "error";
                        break;

                      //colors for Auftragsbearbeitung drinking water
                      case "Auftrag erhalten":
                      case "Labor-Termin bestätigen":
                      case "Prüfbericht abwarten":
                        chipColor ="error";
                        break;
                      case "Labortermin abwarten":
                      case "Termin an Kunden melden":
                      case "Outlook-Termin eintragen":
                        chipStyle = {
                        backgroundColor: colors.blueAccent[900],
                        color: "black",
                        };
                        break;
                      case "Prüfbericht ablegen":
                      case "Prüfbericht übertragen":
                      case "Mieterinfo erstellen":
                      case "Rechnungsfreigabe":
                      case "Rechnung erstellen":
                      case "Rechnung versenden":
                      case "Meldung Gesundheitsamt":
                          chipStyle = {
                        backgroundColor: colors.blueAccent[300],
                        color: "black",
                        };
                        break;


                      default:
                        chipColor = "success";
                    }
                  }else if(column.field === "userStatus") {
                    switch (params.value) {
                      case "Aktiv":
                        chipColor = "success";
                        break;
                      case "Deaktiviert":
                        chipColor = "error";
                        break;
                      default:
                        chipColor = "default";
                    } 
                  }else if(column.field == "actionStatus") {
                    switch (params.value) {
                      case "Vorgeschlagen":
                        chipColor = "warning";
                        break;
                      case "Undurchführbar":
                        chipColor = "error";
                        break;
                      case "Abgelehnt":
                        chipColor = "error";
                        break;
                      case "Angenommen":
                        chipStyle = { backgroundColor: '#1976d2', color: 'white' }; // Blue background
                        break;
                      case "Durchgeführt":
                        chipColor = "success";
                        break;
                    }
                  } else if(column.field === "disturbanceStatus")
                  {
                      switch(params.value){
                        case "Messwertstörung":
                        case "Komponentenstörung":
                          chipColor = "warning";
                        break;
                        case "Komponentendefekt":
                        case "Mikrobiologie erhöht":
                          chipColor = "error";
                        break;

                       default:
                        chipColor = "success"; 
                      }
                  }
                   else {
                    if (
                      params.value === "urgent" ||
                      params.value === "Verzögert" ||
                      params.value === "Falsch"
                    ) {
                      chipColor = "error";
                    } else if (params.value === "alert") {
                      chipColor = "warning";
                    }
                  }

                  return (
                    <Table>
                      <TableBody>
                        <TableRow>
                          <TableCell>
                            <Chip
                              color={chipColor}
                              label={params.value}
                              size="medium"
                              style={chipStyle}
                            />
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  );
                },
              };
            }
            if (column.field === "end_date") {
              return {
                ...column,
                renderCell: (params: any) => {
                  return formatDate(params.value);
                },
              };
            }
            if (parentPath || customLink) {
              return {
                ...column,
                renderCell: (params: any) => {
                  const resolvedPath = typeof parentPath === 'function'
                    ? parentPath(params.row)
                    : parentPath;
                  const link = resolvedPath
                    ? `/${resolvedPath}/${params.id}`
                    : customLink
                      ? getLinkWithIds(customLink, params.row)
                      : "";

                  return (
                    <MuiLink
                      component={RouterLink}
                      to={link}
                      underline="none"
                      color="inherit"
                      width="100%"
                      height="100%"
                      display="flex"
                      alignItems="center"
                    >
                      {params.value}
                    </MuiLink>
                  )
                },
              };
            }
            return column;
          }),
        ]}
        onRowClick={handleRowClick}
        rowCount={rowCountState ?? 0}
        pageSizeOptions={pageSizeOptions}
        paginationModel={paginationModel}
        paginationMode="server"
        onPaginationModelChange={(newModel) => {
          // Update your local paginationModel state here if needed
          setPaginationModel(newModel);

          // Call your onPageChange function to handle page changes
          if (onPageChange) {
            onPageChange(newModel);
          }
        }}
        getRowClassName={(params) =>
          params.row.is_read === false ? "unread-row" : ""
        }
        getCellClassName={getCellClassName}
        getRowHeight={getRowHeight}
        sx={{
          "& .changed-value-cell": { color: "gold !important", fontWeight: "bold" },
          height: '100%',
          minHeight: minHeight ?? defaultMinHeight,
          "& .MuiDataGrid-columnHeader": {
            backgroundColor: colors.blueAccent[700],
            fontSize: "16px",
            whiteSpace: "normal !important",
            wordBreak: "break-word !important",
            textAlign: "center",
            padding: "10px",
          },
          "& .MuiDataGrid-cell": {
            fontSize: "15px",
            whiteSpace: "normal !important", // Enable wrapping
            wordBreak: "break-word !important", // Break long words
            lineHeight: "1.5", // Improve readability
            display: "flex",
            alignItems: "center", // Align content
            padding: "8px", // Add spacing
            hyphens: "auto !important", // Ensure proper word splitting
          },
          "& .MuiDataGrid-virtualScroller::-webkit-scrollbar": {
            height: "12px",
            width: "12px",
            backgroundColor: colors.blueAccent[700],
          },
          "& .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb": {
            backgroundColor: colors.blueAccent[300],
            borderRadius: "6px",
          },
          "& .MuiDataGrid-virtualScroller::-webkit-scrollbar-track": {
            backgroundColor: colors.blueAccent[700],
            width: "12px",

          },
          "& .MuiDataGrid-row:hover": {
            cursor: "pointer",
          },
          "& .MuiDataGrid-main": {
            background: colors.primary[400],
          },
          '& .MuiDataGrid-selectedRowCount': {
            display: 'none',
          },
          "& .MuiDataGrid-row": {
            minHeight: "52px !important", // Ensure rows have minimum height
          },
          "& .MuiDataGrid-virtualScroller": {
            overflowY: data?.length ? "auto" : "hidden",
          },

          // Optional: For Firefox support
          scrollbarWidth: "thin",              // Firefox
          scrollbarColor: `${colors.blueAccent[300]} ${colors.blueAccent[700]}`,
        }}
        localeText={{
          ...translations,
          MuiTablePagination: {
            labelDisplayedRows: ({to, count}: any) => {
              const pageSize = paginationModel.pageSize; // Current page size
              const currentPage = Math.ceil(to / pageSize); // Corrected page number calculation
              const totalPages = Math.ceil(count / pageSize); // Total pages calculation

              return `Seite ${currentPage} von ${totalPages}`;
            },
            labelRowsPerPage: "Zeilen per Seite:",
          },
        }}
        slotProps={{
          filterPanel: {
            filterFormProps: {
              operatorInputProps: {
                disabled: true, // If you only want to disable the operator
                sx: { display: "none" }, // If you want to remove it completely
              },
              deleteIconProps: {
                sx: { display: "none" }, // If you want to remove it completely
              },
            },
            sx: {
              position: "absolute",
              top: "-115px",
              left: "0px",
              borderRadius: "4px",
            },
          },
          pagination: { showFirstButton: true, showLastButton: true },
        }}
        slots={{
          noRowsOverlay: CustomNoRowsOverlay,
          noResultsOverlay: CustomNoRowsOverlay,
          toolbar: CustomToolbar,
        }}
        filterMode={ssrFilter ? "server" : "client"}
        onFilterModelChange={handleFilterModelChange}
        loading={isLoading}
        sortingMode={ssrSort ? "server" : "client"}
        sortModel={sortModel}
        sortingOrder={["asc", "desc"]}
        onSortModelChange={handleSortModelChange}
        filterModel={currentFilterModal ?? { items: [] }}
        disableColumnFilter={disableColumnFilter}
      />
    </Box>
    </>
  );
};

export default DataTable;
